// The app's place lives in the URL, so the back gesture, reloads and the home screen app's restarts keep it:
//   /?f=<folder>[&m=<message id>]          a folder (default Inbox), optionally with a message open
//   /search?q=..&in=..&from=..[&m=<id>]    a search with its filters
//   /starred[?m=<id>]                      the starred mail in every folder, newest first
//   ...&c=new|reply|all|forward&d=<id>     writing a message on top of either (reply, all, forward: to message m),
//                                          the draft on the device it is kept as (d)
//   /rules[?r=<rule id>|new]               the forwarding rules, optionally with one open
//   /settings                              the offline settings: which folders are kept on the device
//   /notifications                         new-mail notifications: this device, the folders, the other devices
//   /outbox                                mail and actions waiting for a connection
//   /drafts[?m=..&c=..&d=..]               the drafts on the device, optionally with one being written on top
//   /auth?code=..&state=..                 the OAuth callback (handled before the app starts)

import { useEffect, useState } from "preact/hooks";
import type { SearchParams } from "./api";

export type ComposeMode = "new" | "reply" | "all" | "forward";

const COMPOSE_MODES: readonly string[] = ["new", "reply", "all", "forward"];

export type Route = (
  | { view: "folder"; folder: string }
  | { view: "search"; search: SearchParams }
  | { view: "starred" }
  | { view: "rules" }
  | { view: "settings" }
  | { view: "notifications" }
  | { view: "outbox" }
  | { view: "drafts" }
) & {
  message: string | null;
  /** Writing a message; a reply or forward is to `message`. Left out when not writing. */
  compose?: ComposeMode;
  /** The id of the draft the message being written is kept as (outgoing.ts). Only with `compose`. */
  draft?: string;
  /** The forwarding rule open in the rules view: its id, or "new". Left out when none is open. */
  rule?: string;
};

const SEARCH_KEYS: Record<string, keyof SearchParams> = {
  q: "q", in: "folder", from: "from", to: "to", st: "sent_to", after: "after", before: "before", att: "has_attachment", del: "include_deleted",
  notrash: "exclude_trash", star: "flagged", exact: "exact", sort: "sort",
};

function composeMode(value: string | null, message: string | null): ComposeMode | undefined {
  if (value === null || !COMPOSE_MODES.includes(value)) return undefined;
  return value !== "new" && !message ? "new" : (value as ComposeMode);
}

export function parseRoute(url: URL): Route {
  const route = parsePlace(url);
  if (route.view === "rules" || route.view === "settings" || route.view === "notifications" || route.view === "outbox") return route;
  const compose = composeMode(url.searchParams.get("c"), route.message);
  if (!compose) return route.view === "drafts" ? { ...route, message: null } : route;
  const draft = url.searchParams.get("d");
  return draft ? { ...route, compose, draft } : { ...route, compose };
}

function parsePlace(url: URL): Route {
  const p = url.searchParams;
  const message = p.get("m");
  if (url.pathname === "/settings") return { view: "settings", message: null };
  if (url.pathname === "/notifications") return { view: "notifications", message: null };
  if (url.pathname === "/outbox") return { view: "outbox", message: null };
  if (url.pathname === "/drafts") return { view: "drafts", message };
  if (url.pathname === "/starred") return { view: "starred", message };
  if (url.pathname === "/rules") {
    const rule = p.get("r");
    return rule ? { view: "rules", message: null, rule } : { view: "rules", message: null };
  }
  if (url.pathname === "/search") {
    const search: SearchParams = {};
    for (const [key, field] of Object.entries(SEARCH_KEYS)) {
      const value = p.get(key);
      if (value === null || value === "") continue;
      if (field === "has_attachment" || field === "include_deleted" || field === "exclude_trash" || field === "flagged" || field === "exact") search[field] = value === "1";
      else if (field === "sort") search.sort = value === "newest" ? "newest" : "relevance";
      else search[field] = value;
    }
    return { view: "search", search, message };
  }
  return { view: "folder", folder: p.get("f") || "Inbox", message };
}

export function routeUrl(route: Route): string {
  const p = new URLSearchParams();
  if (route.view === "settings") return "/settings";
  if (route.view === "notifications") return "/notifications";
  if (route.view === "outbox") return "/outbox";
  if (route.view === "rules") {
    if (route.rule) p.set("r", route.rule);
    return `/rules${route.rule ? `?${p}` : ""}`;
  }
  if (route.view === "folder") {
    if (route.folder !== "Inbox") p.set("f", route.folder);
  } else if (route.view === "search") {
    for (const [key, field] of Object.entries(SEARCH_KEYS)) {
      const value = route.search[field];
      if (value === undefined || value === "" || value === false) continue;
      p.set(key, value === true ? "1" : String(value));
    }
  }
  if (route.message && (route.compose || route.view !== "drafts")) p.set("m", route.message);
  if (route.compose) p.set("c", route.compose);
  if (route.compose && route.draft) p.set("d", route.draft);
  const qs = p.toString();
  const path = route.view === "search" ? "/search" : route.view === "drafts" ? "/drafts" : route.view === "starred" ? "/starred" : "/";
  return `${path}${qs ? `?${qs}` : ""}`;
}

const listeners = new Set<() => void>();
if (typeof window !== "undefined") addEventListener("popstate", () => listeners.forEach((fn) => fn()));

/** Pushes a new history entry (replace=true: replaces the current one). Each pushed entry is marked, so the app
 * knows whether "back" stays inside the app. */
export function navigate(route: Route, replace = false): void {
  const url = routeUrl(route);
  if (url === location.pathname + location.search) return;
  const depth = ((history.state as { depth?: number } | null)?.depth ?? 0) + (replace ? 0 : 1);
  if (replace) history.replaceState({ depth }, "", url);
  else history.pushState({ depth }, "", url);
  listeners.forEach((fn) => fn());
}

/** Back inside the app when the previous entry is ours, else to the given route. */
export function back(fallback: Route): void {
  if (((history.state as { depth?: number } | null)?.depth ?? 0) > 0) history.back();
  else navigate(fallback, true);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(new URL(location.href)));
  useEffect(() => {
    const update = () => setRoute(parseRoute(new URL(location.href)));
    listeners.add(update);
    return () => void listeners.delete(update);
  }, []);
  return route;
}
